import { HttpParams } from "@angular/common/http";
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { BehaviorSubject, map, mergeMap, Observable, share, Subscription, tap } from "rxjs";

import { DeliveryApiService } from "../../../shared-library/delivery-api/delivery-api.service";
import { DirectorySearchPageModel } from "../../../shared-library/delivery-api/models/directory-search-page.model";
import { PaginationOptionsModel } from "../../../shared-library/form-controls/pagination/pagination-options.model";
import { BrowserWindowService } from "../../../shared-library/services/environment/browser-window.service";
import { isRteMarkupEmpty } from "../../../shared-library/utils/rte.utils";
import { ContentItemModel } from "../content-search/models/content-search.model";
import { SearchParametersModel as ContentSearchParameters } from "../content-search/models/search-parameters.model";
import { ContentSearchService } from "../content-search/services/content-search.service";
import { BaseSearchResultsModel } from "../models/base-search-results.model";
import { LockService } from "../services/lock.service";
import { PaginationOptionsFactoryService } from "../services/pagination-options-factory.service";

import { GoogleMapComponent } from "./components/google-map/google-map.component";
import { GoogleMapService } from "./components/google-map/google-map.service";
import { DirectorySearchForm } from "./forms/directory-search.form";
import { DirectoryItemModel } from "./models/directory-item.model";
import { SearchParametersModel } from "./models/search-parameters.model";
import { SearchTabConfigModel } from "./models/search-tab-config.model";
import { DirectorySearchParametersFactoryService } from "./services/directory-search-parameters-factory.service";
import { DirectorySearchService } from "./services/directory-search.service";

@Component({
    selector: "pcg-ng-directory-search",
    templateUrl: "./directory-search.component.html"
})
export class DirectorySearchComponent implements OnInit, OnDestroy {
    public static SELECTOR: string = "pcg-ng-directory-search";

    @ViewChild("mapContainer", { static: false })
    public mapContainerRef: GoogleMapComponent;

    @ViewChild("searchResults")
    public searchResultsRef: ElementRef;

    public searchForm: DirectorySearchForm;

    @Input() public activeSearchTab: string;
    @Input() public searchTabConfig: SearchTabConfigModel;
    public paginationOptions: PaginationOptionsModel;
    public searchParameters$: BehaviorSubject<SearchParametersModel>;
    public searchResult$: Observable<BaseSearchResultsModel<DirectoryItemModel>>;
    public contentSearchResult$: Observable<BaseSearchResultsModel<ContentItemModel>>;
    public datesSpan$ = new BehaviorSubject<{ startDate: string, endDate: string, }>({ startDate: "", endDate: "" });
    public isMapVisible: boolean = true;
    @Output() protected searchTabChange = new EventEmitter<string>();
    protected pageModel$: Observable<DirectorySearchPageModel>;
    protected selectedPageNumber: number = 1;
    protected readonly defaultNoResultsMessage: string = "Sorry, no results were found. Please try changing your search criteria.";

    private formSubscription: Subscription;

    public constructor(
        private _browserWindowService: BrowserWindowService,
        private _paginationOptionsFactory: PaginationOptionsFactoryService,
        private _searchParametersFactory: DirectorySearchParametersFactoryService,
        private _directorySearchService: DirectorySearchService,
        private _deliveryApiService: DeliveryApiService,
        private _lockService: LockService,
        public _googleMapService: GoogleMapService,
        public _contentSearchService: ContentSearchService
    ) {
    }

    public get isLocked$(): Observable<boolean> {
        return this._lockService.isLocked$;
    }

    public ngOnInit(): void {
        this.pageModel$ = this._deliveryApiService.getPage$<DirectorySearchPageModel>(window.location.pathname);

        this.paginationOptions = this._paginationOptionsFactory.createDefaultPaginationOptions();
        this.paginationOptions.showGoToPrevious = true;
        this.paginationOptions.showGoToNext = true;

        const initialParameters = this._searchParametersFactory.createFromQueryString(this._browserWindowService.getQueryString());
        initialParameters.searchTab = this.activeSearchTab;
        initialParameters.templateId = this.searchTabConfig.directoryTemplateId;

        const emptyParameters = new SearchParametersModel();

        this.searchForm = DirectorySearchForm.createForm(initialParameters, emptyParameters);

        (this.searchForm as any).submit = () => {
            const freshFormValue = this.searchForm.instance.value;
            const currentParams = this.searchParameters$.value;
            const newParams = { ...currentParams, ...freshFormValue };
            this.searchParameters$.next(newParams);
        };

        if (initialParameters.startDate || initialParameters.endDate) {
            this.datesSpan$.next({
                startDate: initialParameters.startDate || "",
                endDate: initialParameters.endDate || ""
            });
        }

        this.searchParameters$ = new BehaviorSubject<SearchParametersModel>(initialParameters);

        if (this.searchForm.onFormChanged) {
            this.formSubscription = this.searchForm.onFormChanged.subscribe((newParams: SearchParametersModel) => {
                this.selectedPageNumber = newParams.pageNumber;
                this.searchParameters$.next(newParams);
            });
        }

        this.searchResult$ = this.searchParameters$.pipe(
            tap(() => this._lockService.lock()),
            tap(() => this._googleMapService.clear()),
            tap(parameters => this.setQueryString(parameters)),
            map(parameters => this.mapParameters(parameters)),
            mergeMap(parameters => this._directorySearchService.getSearchResults(parameters)),
            tap(() => this._lockService.unlock()),
            share()
        );

        this.contentSearchResult$ = this.searchParameters$.pipe(
            map(parameters => this.mapContentSearchParameters(parameters)),
            mergeMap(parameters => this._contentSearchService.getSearchResults(parameters)),
            share()
        );

        this._googleMapService.showMap();
    }

    public ngOnDestroy(): void {
        if (this.formSubscription) {
            this.formSubscription.unsubscribe();
        }
    }

    public onSearchTriggered(): void {
        this.searchForm.pageNumber.setValue(1, { emitEvent: false });
        (this.searchForm as any).submit();
    }

    public onToggleMap(): void {
        this.isMapVisible = !this.isMapVisible;

        if (this.isMapVisible) {
            this._googleMapService.showMap();
        } else {
            this._googleMapService.hideMap();
        }
    }

    public getContentResultPageUrl(): string {
        const parameters = this.searchParameters$.getValue();

        const params = new HttpParams()
            .set("searchText", parameters.searchText ?? "")
            .set("sortOption", parameters.sortOption);

        return `${this.searchTabConfig.contentSearchPageUrl}?${params.toString()}`;
    }

    public handleSkipToResults(event: Event): void {
        event.preventDefault();
        const el = this.searchResultsRef.nativeElement;
        el.setAttribute("tabindex", "-1");
        el.focus();
        el.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    protected setPageNumber(pageNumber: number): void {
        this.selectedPageNumber = pageNumber;
        this.searchForm.pageNumber.setValue(pageNumber);
    }

    protected resolveNoResultsMessage(markup: string | null | undefined): string {
        return isRteMarkupEmpty(markup) ? this.defaultNoResultsMessage : (markup as string);
    }

    private mapParameters(parameters: SearchParametersModel): SearchParametersModel {
        return { ...parameters };
    }

    private setQueryString(parameters: SearchParametersModel): void {
        this._browserWindowService.setQueryString(this._searchParametersFactory.serialiseToQueryString(parameters));
    }

    private mapContentSearchParameters(parameters: SearchParametersModel): ContentSearchParameters {
        const mapped = new ContentSearchParameters();
        mapped.searchContext = this.searchTabConfig.contentSearchContext || "1118";
        mapped.searchText = parameters.searchText;

        mapped.sortOption = this.mapContentSearchSortOption(parameters.sortOption);

        mapped.pageSize = 3;
        mapped.pageNumber = parameters.pageNumber;
        return mapped;
    }

    private mapContentSearchSortOption(sortOption: string): string {
        switch (sortOption) {
            case "Rank;asc":
                return "0";
            case "Name;asc":
                return "1";
            case "Name;desc":
                return "2";
            default:
                return "0";
        }
    }
}
